import { RECORDED_CONTENT_API } from "../RecordedContent.Apis";
import { IQuestionnaire } from "./Questionnaire/useQuestionnaire.interfaces";
import { IExamForm } from "./useExamForm.interfaces";

/**
 * @param API_REQUEST - Axios instance with base URL and default headers for API calls.
 * @param username - username of the user.
 * @param questionnaireUid - uuid of the questionnaire.
 * @returns {Promise} A promise that resolves to the status of the last questionnaire attempt.
 */
export const fetchQuestionnaireStatus = async ({
  contentUuid,
  API_REQUEST,
  username,
  questionnaireUid,
}: {
  contentUuid: string;
  API_REQUEST: any; // TODO: Replace with AxiosInstance after axios library is installed
  username: string;
  questionnaireUid?: string;
}): Promise<IExamForm.IStatus> => {
  try {
    const response = await API_REQUEST.get(
      RECORDED_CONTENT_API.fetch_questionnaire_status,
      {
        params: {
          recorded_content_uuid: contentUuid,
          username,
          questionairre_uid: questionnaireUid,
        },
      },
    );

    return response?.data?.data?.last_questionnaire_attempt;
  } catch (err) {
    console.log("Failed to fetch questionnaire status", err);
    throw err;
  }
};

/**
 * @param uuid - uuid of the questionnaire.
 * @param API_REQUEST - Axios instance with base URL and default headers for API calls.
 * @returns {Promise} A promise that resolves to the details of a questionnaire.
 */
export const fetchQuestionnaireDetails = async ({
  uuid,
  API_REQUEST,
}: {
  uuid: string;
  API_REQUEST: any; // TODO: Replace with AxiosInstance after axios library is installed
}): Promise<{
  questionairre: {
    questions: Array<IQuestionnaire.IQuestion>;
  } & IQuestionnaire.IExamSettings;
}> => {
  try {
    const response = await API_REQUEST.get(
      RECORDED_CONTENT_API.fetch_questionnaire_details,
      {
        params: {
          questionairre_uid: uuid,
        },
      },
    );
    return response?.data?.data;
  } catch (err) {
    console.log("Failed to fetch questionnaire details", err);
    throw err;
  }
};

/**
 * @param questionnaireUid - uuid of the questionnaire.
 * @param API_REQUEST - Axios instance with base URL and default headers for API calls.
 * @param username - username of the user.
 * @returns {Promise} A promise that returns the responses of already submitted form.
 */
export const fetchFormResponses = async ({
  contentUuid,
  questionnaireUid,
  API_REQUEST,
  username,
}: {
  contentUuid: string;
  questionnaireUid: string;
  API_REQUEST: any; // TODO: Replace with AxiosInstance after axios library is installed
  username: string;
}): Promise<IQuestionnaire.IQuestionnaireResponse> => {
  try {
    const response = await API_REQUEST.get(
      RECORDED_CONTENT_API.fetch_form_responses,
      {
        params: {
          recorded_content_uuid: contentUuid,
          username,
          questionairre_uid: questionnaireUid,
        },
      },
    );
    return response?.data?.data?.form_and_responses;
  } catch (err) {
    console.log("Failed to fetch form responses", err);
    throw err;
  }
};

/**
 * @param examInfo - An object containing details of the exam for which we want to add attempt.
 * @param username - username of the user.
 * @param API_REQUEST - Axios instance with base URL and default headers for API calls.
 * @returns {Promise} A promise that returns an object containing uuid of the new attempt.
 */
export const addExamAttempt = async ({
  contentUuid,
  examInfo,
  username,
  API_REQUEST,
}: {
  contentUuid: string;
  examInfo: {
    questionairre_uid?: string;
    total_questions?: number;
    questions_uid_list?: Array<string>;
    correct_answer_mark?: number;
    incorrect_answer_mark?: number;
    passing_marks?: number;
    total_marks?: number;
  };
  username?: string;
  API_REQUEST: any; // TODO: Replace with AxiosInstance after axios library is installed
}): Promise<IQuestionnaire.IQuestionnaireResponse> => {
  try {
    const response = await API_REQUEST.post(
      RECORDED_CONTENT_API.add_exam_attempt,
      {
        recorded_content_uuid: contentUuid,
        username,
        ...examInfo,
      },
    );
    return response?.data?.data;
  } catch (err) {
    console.log("Failed to add exam attempt", err);
    throw err;
  }
};

/**
 * @param examInfo - An object containing details of the exam for which we want to add attempt.
 * @param username - username of the user.
 * @param API_REQUEST - Axios instance with base URL and default headers for API calls.
 * @returns {Promise} A promise that returns the object cntaining result details of the questionnaire.
 */
export const submitExamOrForm = async ({
  contentUuid,
  examInfo,
  username,
  API_REQUEST,
}: {
  contentUuid: string;
  examInfo: {
    questionairre_uid?: string;
    total_questions?: number;
    attempt_uid?: string;
    time_taken?: number;
    questions_and_responses?: Array<IQuestionnaire.IAnswer>;
  };
  username?: string;
  API_REQUEST: any; // TODO: Replace with AxiosInstance after axios library is installed
}): Promise<IQuestionnaire.IResult> => {
  try {
    const response = await API_REQUEST.post(
      RECORDED_CONTENT_API.submit_form_or_exam,
      {
        recorded_content_uuid: contentUuid,
        username,
        ...examInfo,
      },
    );
    return response?.data?.data?.attempt;
  } catch (err) {
    console.log("Failed to submit questionnaire", err);
    throw err;
  }
};
